import { RefObject, useEffect, useState } from 'react';

export interface IUseScrollNavigationProps {
	enabled: boolean;
	itemCount: number;
	pageSize?: number;
	trackRef?: RefObject<HTMLElement | null>;
}

export interface IUseScrollNavigationReturn {
	currentPage?: number;
	hasNavigation: boolean;
	nextEnabled?: boolean;
	previousEnabled?: boolean;
	onPreviousClick?: () => void;
	onNextClick?: () => void;
}

export const useScrollNavigation = ({
	enabled,
	itemCount,
	trackRef,
	pageSize = 3
}: IUseScrollNavigationProps): IUseScrollNavigationReturn => {
	const [currentPage, setCurrenPage] = useState(0);

	const items = trackRef?.current?.querySelectorAll('button');
	const hasMultiplePages = items && items.length > pageSize;
	const pages = itemCount / pageSize;
	const previousEnabled = currentPage > 0;
	const nextEnabled = currentPage < pages - 1;

	const onPreviousClick = (): void => {
		if (previousEnabled) {
			const firstItemIndex = (currentPage - 1) * pageSize;
			setCurrenPage((previousValue) => previousValue - 1);
			changeAllTabIndex(-1, firstItemIndex);
		}
	};

	const onNextClick = (): void => {
		if (nextEnabled) {
			const firstItemIndex = (currentPage + 1) * pageSize;
			setCurrenPage((previousValue) => previousValue + 1);
			changeAllTabIndex(-1, firstItemIndex);
		}
	};

	const onFocus = (index: number): void => {
		const itemPageLocation = Math.floor(index / pageSize);
		// changeAllTabIndex(0);
		setCurrenPage(itemPageLocation);
	};

	// Disable/enable tab index to focus on correct item/page
	const changeAllTabIndex = (number: number, excludeItem?: number): void => {
		if (hasMultiplePages) {
			// eslint-disable-next-line unicorn/no-for-loop
			for (let index = 0; index < items.length; index++) {
				if (typeof excludeItem === 'number') {
					const itemInPage = index >= excludeItem && index < excludeItem + pageSize;

					if (itemInPage) {
						items[index].tabIndex = 0;
					} else {
						items[index].tabIndex = number;
					}
				} else {
					items[index].tabIndex = number;
				}
			}
		}
	};

	useEffect(() => {
		const controller = new AbortController();

		if (hasMultiplePages) {
			// eslint-disable-next-line unicorn/no-for-loop
			for (let index = 0; index < items.length; index++) {
				items[index].addEventListener('focus', () => onFocus(index), { signal: controller.signal });
			}
		}

		return () => {
			controller.abort();
		};
	}, [trackRef?.current, pageSize]);

	useEffect(() => {
		setCurrenPage(0);
	}, [pageSize]);

	if (!enabled) return { hasNavigation: false };

	return {
		hasNavigation: true,
		currentPage,
		nextEnabled,
		previousEnabled,
		onPreviousClick,
		onNextClick
	};
};

export default useScrollNavigation;
